import { z } from 'zod'

/**
 * Brand assets that admins upload once and the whole platform reads: the header logo, the app
 * icon set, the favicon and the social share image. Files live in R2 under `branding/<kind>`;
 * the metadata below is stored in the `site.branding` setting so the SPA can build URLs without
 * an extra request.
 */
export const BRANDING_KINDS = [
  'logo',
  'logo_dark',
  'mark',
  'favicon',
  'icon_192',
  'icon_512',
  'icon_maskable',
  'apple_touch',
  'og_image',
] as const
export type BrandingKind = (typeof BRANDING_KINDS)[number]

export interface BrandingDef {
  label: string
  help: string
  /** Accepted MIME types. */
  accepts: readonly string[]
  /** Recommended pixel size, for the admin page. */
  recommended: string
  /** Square assets are generated in the browser from the mark; they can still be overridden. */
  derived?: boolean
  /** Static file served when nothing has been uploaded. */
  fallback: string | null
}

export const BRANDING_DEFS: Record<BrandingKind, BrandingDef> = {
  logo: {
    label: 'Logo',
    help: 'Shown in the site header, the portal and admin sidebars, receipts and emails. Wide or square; SVG keeps it crisp.',
    accepts: ['image/svg+xml', 'image/png', 'image/webp'],
    recommended: 'SVG, or PNG at least 400px tall',
    fallback: null,
  },
  logo_dark: {
    label: 'Logo for dark backgrounds',
    help: 'Optional. Used instead of the logo when the site is in dark mode.',
    accepts: ['image/svg+xml', 'image/png', 'image/webp'],
    recommended: 'Same artwork as the logo, light colours',
    fallback: null,
  },
  mark: {
    label: 'App icon',
    help: 'A square symbol. The favicon and every PWA icon size are generated from it when you upload.',
    accepts: ['image/svg+xml', 'image/png'],
    recommended: 'Square, 1024×1024 or SVG',
    fallback: '/icons/icon-512.png',
  },
  favicon: {
    label: 'Favicon',
    help: 'Browser tab icon.',
    accepts: ['image/svg+xml', 'image/png', 'image/x-icon', 'image/vnd.microsoft.icon'],
    recommended: 'SVG or 64×64 PNG',
    derived: true,
    fallback: '/icons/favicon.svg',
  },
  icon_192: {
    label: 'Home-screen icon (192)',
    help: 'Android and desktop install icon.',
    accepts: ['image/png'],
    recommended: '192×192 PNG',
    derived: true,
    fallback: '/icons/icon-192.png',
  },
  icon_512: {
    label: 'Home-screen icon (512)',
    help: 'Splash screen and store listing icon.',
    accepts: ['image/png'],
    recommended: '512×512 PNG',
    derived: true,
    fallback: '/icons/icon-512.png',
  },
  icon_maskable: {
    label: 'Maskable icon',
    help: 'Android adaptive icon: artwork inside the centre 80% on a solid background.',
    accepts: ['image/png'],
    recommended: '512×512 PNG, padded',
    derived: true,
    fallback: '/icons/icon-maskable-512.png',
  },
  apple_touch: {
    label: 'Apple touch icon',
    help: 'iOS home-screen icon. No transparency.',
    accepts: ['image/png'],
    recommended: '180×180 PNG, opaque',
    derived: true,
    fallback: '/icons/icon-192.png',
  },
  og_image: {
    label: 'Share image',
    help: 'Preview image when a link to the site is shared on social media or in messages.',
    accepts: ['image/png', 'image/jpeg', 'image/webp'],
    recommended: '1200×630 PNG or JPEG',
    fallback: null,
  },
}

export const brandingAssetSchema = z.object({
  contentType: z.string().max(80),
  sizeBytes: z.number().int().nonnegative(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  /** Changes on every upload; used as the cache-busting query string. */
  version: z.string().min(1).max(40),
  updatedAt: z.string(),
})
export type BrandingAsset = z.infer<typeof brandingAssetSchema>

export const brandingSettingSchema = z.object({
  assets: z.partialRecord(z.enum(BRANDING_KINDS), brandingAssetSchema).default({}),
  /** Solid colour behind padded icons (maskable, Apple touch). */
  iconBackground: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#7c3aed'),
})
export type BrandingSetting = z.infer<typeof brandingSettingSchema>

export const MAX_BRANDING_BYTES = 2 * 1024 * 1024

/** Public URL for a brand asset, or the built-in fallback when nothing has been uploaded. */
export function brandingUrl(
  kind: BrandingKind,
  setting: BrandingSetting | undefined,
  base = '',
): string | null {
  const asset = setting?.assets[kind]
  if (asset) return `${base}/api/v1/public/branding/${kind}?v=${encodeURIComponent(asset.version)}`
  const fb = BRANDING_DEFS[kind].fallback
  return fb ? `${base}${fb}` : null
}
